<script lang="ts">
  import Divider from '$components/ui/divider.svelte';
  import Icon from '$components/ui/icon/icon.svelte';
  import type * as Types from '$gql/storefront/schemas';
  import { cart } from '$lib/cart';
  import { t } from '$lib/i18n';
  import { formatCurrency } from '$lib/utils/currency';

  export let shouldShowTax: boolean;
  export let total: number;

  type CartDiscountAllocation = {
    __typename?: 'CartCodeDiscountAllocation';
    code: string;
    discountedAmount: {
      __typename?: 'MoneyV2';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      amount: any;
      currencyCode: Types.CurrencyCode;
    };
  };

  type CartDiscountCode = {
    __typename?: 'CartDiscountCode';
    applicable: boolean;
    code: string;
  };

  let couponDiscounts: CartDiscountAllocation[] = [];
  let checkoutDiscounts: CartDiscountCode[] = [];

  $: compareAtSubtotal =
    $cart?.lines.nodes.reduce((acc, line) => {
      const compareAtPrice =
        line.merchandise.compareAtPrice?.amount ?? line.merchandise.price.amount;
      const quantity = line.quantity;

      return acc + compareAtPrice * quantity;
    }, 0) || 0;

  const sumCouponAmounts = (coupons: CartDiscountAllocation[]) => {
    const summedCoupons = coupons.reduce(
      (acc, { code, discountedAmount }) => {
        const { amount, currencyCode, __typename } = discountedAmount;

        if (acc[code]) {
          // If the code already exists, add to the existing amount

          acc[code].discountedAmount.amount =
            (Number(acc[code].discountedAmount.amount) || 0) + Number(amount);
        } else {
          // If the code does not exist, create a new entry
          acc[code] = {
            code,
            discountedAmount: {
              __typename,
              amount,
              currencyCode
            }
          };
        }

        return acc;
      },
      {} as Record<string, CartDiscountAllocation>
    );

    return Object.values(summedCoupons);
  };

  $: {
    const couponArray = ($cart?.discountAllocations || []).filter(
      (discount) => discount.__typename === 'CartCodeDiscountAllocation'
    );

    couponDiscounts = sumCouponAmounts(couponArray);

    checkoutDiscounts = ($cart?.discountCodes || []).filter(
      (code) => code.applicable && !couponArray.map((c) => c.code).includes(code.code)
    );
  }

  // NOTE: the total is being calculated manually, so we need to manually deduct the discounts
  $: totalCouponAmount =
    couponDiscounts?.reduce((a, b) => a + Number(b.discountedAmount.amount), 0) || 0;
  $: cartTotal = couponDiscounts && couponDiscounts.length > 0 ? total - totalCouponAmount : total;
</script>

{#if $cart}
  <div class="mt-2 rounded-sm border border-primary/4 bg-primary/2 p-2.5">
    <div class="flex flex-col gap-2">
      <div class="flex items-center justify-between text-sm">
        <p>
          {#if $cart?.totalQuantity === 1}
            {$t('cart.subtotal-one')}
          {:else}
            {$t('cart.subtotal-multiple', { value: $cart.totalQuantity.toString() })}
          {/if}
        </p>
        <div class="flex items-center gap-1">
          {#if compareAtSubtotal > total}
            <span class="line-through opacity-40 tabular-nums">
              {$formatCurrency($cart.cost.totalAmount.currencyCode, compareAtSubtotal)}
            </span>
          {/if}
          <span class="tabular-nums">
            {$formatCurrency(
              $cart.cost.subtotalAmount.currencyCode,
              $cart.cost.subtotalAmount.amount
            )}
          </span>
        </div>
      </div>

      {#if shouldShowTax}
        <div class="flex items-center justify-between text-sm">
          <p>
            {#if $cart.cost.totalTaxAmountEstimated}
              {$t('general.estimated')}
            {/if}
            {$t('cart.tax')}
          </p>
          <p class="tabular-nums">
            {$formatCurrency(
              $cart.cost.subtotalAmount.currencyCode,
              $cart.cost.totalTaxAmount?.amount ?? 0
            )}
          </p>
        </div>
      {/if}

      <div class="flex items-center justify-between text-sm">
        <p>{$t('cart.estimated-shipping')}</p>
        <p>{$t('amount.free')}</p>
      </div>

      {#if couponDiscounts && couponDiscounts.length > 0}
        {#each couponDiscounts as coupon}
          <div class="flex items-center justify-between text-sm">
            <span class="flex items-center uppercase">
              <Icon icon="discount" size="md" class="text-accent" />
              {coupon.code}
            </span>
            <p>
              -{$formatCurrency(
                coupon.discountedAmount.currencyCode,
                coupon.discountedAmount.amount
              )}
            </p>
          </div>
        {/each}
      {/if}

      {#if checkoutDiscounts && checkoutDiscounts.length > 0}
        {#each checkoutDiscounts as coupon}
          <div class="flex items-center justify-between text-sm">
            <span class="flex items-center uppercase">
              <Icon icon="discount" size="md" class="text-accent" />
              {coupon.code}
            </span>
            <p class="opacity-40">
              {$t('discount.cart.applied-at-checkout')}
            </p>
          </div>
        {/each}
      {/if}

      <Divider />

      <!-- Total -->
      <div class="flex items-center justify-between text-sm">
        <p>{$t('cart.total')}</p>
        <p class="tabular-nums">
          {$formatCurrency($cart.cost.subtotalAmount.currencyCode, cartTotal)}
        </p>
      </div>
    </div>
  </div>
{/if}
